// NewConstructionPanel — the three-card affordable-housing module.
//
// Renders directly in the Property Workspace when `property.deal_type ===
// "new_construction"`. Three collapsible cards stacked vertically:
//   1. BuildTracker (13-stage milestones + budget variance)
//   2. Philly Intelligence (OZ + zoning + tax abatement analysis)
//   3. Affordable Buyer Qualifier (AMI + FHA/PHFA/VA/USDA scoring)
//
// All three cards are self-contained — they fetch their own data from
// the public/authenticated endpoints and render loading / error /
// success states independently.

import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Hammer, MapPin, Users, ChevronDown, ChevronUp, Check, Circle,
  Loader2, DollarSign, TreePine, ShieldCheck, AlertCircle,
  Clock, TrendingUp, Home, Zap, Repeat,
} from "lucide-react";

const $ = (n) => (n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`);

const STAGE_STATUS_STYLE = {
  pending:     { color: "#94A3B8", bg: "#F1F5F9", label: "Pending",     icon: Circle },
  in_progress: { color: "#0EA5E9", bg: "#E0F2FE", label: "In Progress", icon: Clock },
  complete:    { color: "#10B981", bg: "#D1FAE5", label: "Complete",    icon: Check },
  blocked:     { color: "#EF4444", bg: "#FEE2E2", label: "Blocked",     icon: AlertCircle },
};

export default function NewConstructionPanel({ property }) {
  return (
    <div className="space-y-4" data-testid="new-construction-panel">
      <VelocityPlayCard property={property} />
      <BuildTrackerCard propertyId={property.id} />
      <PhillyIntelCard property={property} />
      <BuyerQualifierCard />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CARD 0 · THE VELOCITY PLAY (in-app version of /middleton calculator,
//         pre-loaded from THIS property's own numbers)
// ═══════════════════════════════════════════════════════════════════════

function VelocityPlayCard({ property }) {
  const [expanded, setExpanded] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scenarioLabel, setScenarioLabel] = useState("");
  // Local overrides — start blank so the property's own numbers drive the calc.
  const [overrides, setOverrides] = useState({
    construction_cost: "",
    units: "",
    rent_per_unit: "",
    refi_ltv_pct: 75,
    refi_rate_pct: 6.0,
    philly_tax_abatement: true,
  });

  const loadScenarios = useCallback(async () => {
    try {
      const res = await api.get(`/v3/properties/${property.id}/velocity-scenarios`);
      setScenarios(res.data?.scenarios || []);
    } catch { /* silent */ }
  }, [property.id]);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  const saveScenario = async () => {
    setSaving(true);
    try {
      const inputs = {
        construction_cost:    overrides.construction_cost || undefined,
        units:                overrides.units || undefined,
        rent_per_unit:        overrides.rent_per_unit || undefined,
        refi_ltv_pct:         overrides.refi_ltv_pct,
        refi_rate_pct:        overrides.refi_rate_pct,
        philly_tax_abatement: overrides.philly_tax_abatement,
      };
      const res = await api.post(
        `/v3/properties/${property.id}/velocity-scenarios`,
        { label: scenarioLabel.trim() || undefined, inputs },
      );
      toast.success(`Saved: ${res.data.label}`);
      setScenarioLabel("");
      loadScenarios();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (scenarioId, label) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const res = await fetch(
        `${backend}/api/v3/properties/${property.id}/velocity-scenarios/${scenarioId}/pdf`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error("PDF fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `velocity-play-${(label || "scenario").replace(/[^a-z0-9]/gi, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF download failed");
    }
  };

  const deleteScenario = async (sid) => {
    if (!window.confirm("Delete this scenario?")) return;
    try {
      await api.delete(`/v3/properties/${property.id}/velocity-scenarios/${sid}`);
      loadScenarios();
    } catch { toast.error("Delete failed"); }
  };

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        purchase_price:      property.asking_price || undefined,
        arv:                 property.estimated_arv || undefined,
        construction_cost:   overrides.construction_cost || undefined,
        units:               overrides.units || undefined,
        rent_per_unit:       overrides.rent_per_unit || undefined,
        refi_ltv_pct:        overrides.refi_ltv_pct,
        refi_rate_pct:       overrides.refi_rate_pct,
        philly_tax_abatement: overrides.philly_tax_abatement,
      };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/middleton/calc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setResult(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [property.asking_price, property.estimated_arv, overrides]);

  useEffect(() => { run(); }, [run]);

  const verdictColor = { GREEN: "#059669", YELLOW: "#B45309", RED: "#B91C1C" }[result?.verdict] || "#64748B";
  const verdictBg    = { GREEN: "#ECFDF5", YELLOW: "#FEF3C7", RED: "#FEE2E2" }[result?.verdict] || "#F1F5F9";

  return (
    <Card
      icon={<Zap className="w-4 h-4" />}
      title="The Velocity Play"
      subtitle="Build → Refi → IUL Loop → Redeploy · Modeled on this property"
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
      testid="velocity-play-card"
    >
      {(!property.asking_price || !property.estimated_arv) && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-900">
          <b>Missing property data:</b>{" "}
          {!property.asking_price && "Asking Price. "}
          {!property.estimated_arv && "Estimated ARV. "}
          Add these on the property to see the full play — defaults will fill in the rest.
        </div>
      )}

      {/* Overrides input row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <MiniField label="Construction Cost">
          <input type="number" value={overrides.construction_cost}
                 onChange={(e) => setOverrides((o) => ({ ...o, construction_cost: e.target.value }))}
                 placeholder={String(810000)}
                 data-testid="velocity-construction"
                 className="w-full rounded border border-slate-200 px-2 py-1.5 text-[13px] tabular-nums"/>
        </MiniField>
        <MiniField label="# Units">
          <input type="number" value={overrides.units}
                 onChange={(e) => setOverrides((o) => ({ ...o, units: e.target.value }))}
                 placeholder="6" min={1} max={200}
                 data-testid="velocity-units"
                 className="w-full rounded border border-slate-200 px-2 py-1.5 text-[13px] tabular-nums"/>
        </MiniField>
        <MiniField label="Rent / unit / mo">
          <input type="number" value={overrides.rent_per_unit}
                 onChange={(e) => setOverrides((o) => ({ ...o, rent_per_unit: e.target.value }))}
                 placeholder="1525"
                 data-testid="velocity-rent"
                 className="w-full rounded border border-slate-200 px-2 py-1.5 text-[13px] tabular-nums"/>
        </MiniField>
        <MiniField label="Refi Rate %">
          <input type="number" step="0.1" value={overrides.refi_rate_pct}
                 onChange={(e) => setOverrides((o) => ({ ...o, refi_rate_pct: Number(e.target.value) }))}
                 data-testid="velocity-rate"
                 className="w-full rounded border border-slate-200 px-2 py-1.5 text-[13px] tabular-nums"/>
        </MiniField>
      </div>

      {loading && !result && <SkeletonRow />}
      {result && (
        <>
          {/* Verdict */}
          <div className="rounded-lg border p-3 mb-3 flex items-center justify-between"
               style={{ background: verdictBg, borderColor: verdictColor + "40" }}
               data-testid="velocity-verdict">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-widest" style={{ color: verdictColor }}>
                Verdict · {result.verdict}
              </div>
              <div className="text-[12.5px] text-slate-800 mt-0.5">{result.verdict_reason}</div>
            </div>
            <div className="text-right">
              <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">DSCR</div>
              <div className="text-[22px] font-bold tabular-nums" style={{ color: verdictColor }}>{result.dscr}</div>
            </div>
          </div>

          {/* Big 4 numbers */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <SummaryStat label="Equity Created" value={`$${Math.round(result.build_phase.equity_created).toLocaleString()}`} tone="green" />
            <SummaryStat label="Refi Cashout" value={`$${Math.round(result.refi_phase.net_cashout_to_pocket).toLocaleString()}`} tone="amber" />
            <SummaryStat label="Cashflow / mo" value={`$${Math.round(result.cashflow.net_cashflow_monthly).toLocaleString()}`} tone="green" />
            <SummaryStat label="IUL Loan Avail" value={`$${Math.round(result.iul_loop.loan_available_day_30).toLocaleString()}`} tone="amber" />
          </div>

          {/* Phase breakdown — 3 compact rows */}
          <div className="space-y-2 mt-3">
            <PhaseMiniRow
              icon={<Home className="w-3.5 h-3.5" />}
              title="Build Phase"
              stats={[
                { label: "All-In", value: `$${Math.round(result.build_phase.all_in_cost).toLocaleString()}` },
                { label: "ARV", value: `$${Math.round(result.build_phase.arv).toLocaleString()}` },
                { label: "Equity %", value: `${result.build_phase.equity_pct}%` },
              ]}
            />
            <PhaseMiniRow
              icon={<Repeat className="w-3.5 h-3.5" />}
              title="Cashout Refi"
              stats={[
                { label: "New Loan", value: `$${Math.round(result.refi_phase.new_loan_amount).toLocaleString()}` },
                { label: "Gross Cash", value: `$${Math.round(result.refi_phase.gross_cashout).toLocaleString()}` },
                { label: "Net (tax-free)", value: `$${Math.round(result.refi_phase.net_cashout_to_pocket).toLocaleString()}`, tone: "green" },
              ]}
            />
            <PhaseMiniRow
              icon={<Zap className="w-3.5 h-3.5" />}
              title="IUL Wealth Loop"
              stats={[
                { label: "Deposit", value: `$${Math.round(result.iul_loop.deposit_amount).toLocaleString()}` },
                { label: "Loan @ Day 30", value: `$${Math.round(result.iul_loop.loan_available_day_30).toLocaleString()}`, tone: "amber" },
                { label: "1yr Growth", value: `$${Math.round(result.iul_loop.projected_1yr_growth_on_deposit).toLocaleString()}`, tone: "green" },
              ]}
            />
          </div>

          {/* 5-year cascade summary */}
          {result.cascade && result.cascade.length > 0 && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                5-Year Cascade · Run the play annually
              </div>
              <div className="text-[13px] text-slate-800">
                Year 5: <b>{result.cascade[4].portfolio_units} units</b>,{" "}
                <span className="text-emerald-700 font-bold">${Math.round(result.cascade[4].portfolio_net_cashflow_monthly).toLocaleString()}/mo</span> net,{" "}
                IUL balance <b>${Math.round(result.cascade[4].iul_balance).toLocaleString()}</b>
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
            <input
              type="text" value={scenarioLabel}
              onChange={(e) => setScenarioLabel(e.target.value)}
              placeholder="Label this scenario (e.g. 'Aggressive 75% LTV')"
              data-testid="velocity-scenario-label"
              className="flex-1 min-w-[220px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
            <button
              type="button" onClick={saveScenario} disabled={saving}
              data-testid="velocity-save-scenario"
              className="inline-flex items-center gap-1.5 rounded-lg text-white text-[12.5px] font-semibold px-3.5 py-2 shadow-md disabled:opacity-60"
              style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
            >
              {saving ? "Saving…" : "Save Scenario"}
            </button>
            <a href="/middleton" target="_blank" rel="noreferrer"
               className="text-[12px] font-semibold text-amber-700 hover:text-amber-900 ml-auto">
              Open full calculator →
            </a>
          </div>
          <p className="mt-2 text-[10.5px] text-slate-500">
            Modeling only — see disclaimers on the /middleton landing.
          </p>

          {/* ── Saved scenarios ────────────────────────────────── */}
          {scenarios.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">
                  Saved Scenarios · {scenarios.length}
                </div>
                <div className="text-[10.5px] text-slate-400">Download → send to seller / lender</div>
              </div>
              <div className="space-y-1.5" data-testid="velocity-scenarios-list">
                {scenarios.map((s) => {
                  const vStyle = { GREEN: "text-emerald-700", YELLOW: "text-amber-700", RED: "text-rose-700" }[s.verdict] || "text-slate-600";
                  return (
                    <div key={s.id}
                         data-testid={`velocity-scenario-${s.id}`}
                         className="rounded-lg border border-slate-200 bg-white p-2.5 flex items-center gap-3 hover:border-amber-300 transition-colors">
                      <div className={"text-[10.5px] font-bold uppercase " + vStyle}>{s.verdict || "—"}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-bold text-slate-900 truncate">{s.label}</div>
                        <div className="text-[10.5px] text-slate-500">
                          DSCR {s.dscr} · Equity ${Math.round(s.headline.equity_created).toLocaleString()} · Cashflow ${Math.round(s.headline.net_cashflow_monthly).toLocaleString()}/mo · Cashout ${Math.round(s.headline.net_cashout).toLocaleString()}
                        </div>
                      </div>
                      <button type="button"
                              onClick={() => downloadPdf(s.id, s.label)}
                              data-testid={`velocity-download-${s.id}`}
                              className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 px-2 py-1 whitespace-nowrap">
                        Download PDF
                      </button>
                      <button type="button"
                              onClick={() => deleteScenario(s.id)}
                              data-testid={`velocity-delete-${s.id}`}
                              className="text-[11px] text-slate-400 hover:text-rose-600 px-1">
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function PhaseMiniRow({ icon, title, stats }) {
  const toneClasses = { slate: "text-slate-900", amber: "text-amber-700", green: "text-emerald-700" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 flex items-center gap-3">
      <div className="h-7 w-7 rounded-md grid place-items-center text-white shrink-0"
           style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
        {icon}
      </div>
      <div className="text-[12.5px] font-bold text-slate-900 w-32 shrink-0">{title}</div>
      <div className="flex-1 grid grid-cols-3 gap-2 text-[11.5px]">
        {stats.map((s, i) => (
          <div key={i}>
            <div className="text-slate-500 truncate">{s.label}</div>
            <div className={"font-bold tabular-nums " + (toneClasses[s.tone] || toneClasses.slate)}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CARD 1 · BUILD TRACKER
// ═══════════════════════════════════════════════════════════════════════

function BuildTrackerCard({ propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [editingKey, setEditingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v3/build-tracker/${propertyId}`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load BuildTracker");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const patchStage = async (stageKey, updates) => {
    try {
      const res = await api.patch(`/v3/build-tracker/${propertyId}/stage/${stageKey}`, updates);
      // Refresh full doc so summary + all stages stay in sync
      setData((prev) => {
        if (!prev) return prev;
        const stages = prev.stages.map((s) => (s.key === stageKey ? res.data.stage : s));
        return { ...prev, stages, summary: res.data.summary };
      });
      toast.success(`${res.data.stage.label} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update stage");
    }
  };

  return (
    <Card
      icon={<Hammer className="w-4 h-4" />}
      title="BuildTracker"
      subtitle="13-stage milestone map · Lot → CO → Sold"
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
      testid="build-tracker-card"
    >
      {loading && <SkeletonRow />}
      {data && !loading && (
        <>
          {/* Summary strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <SummaryStat label="Progress" value={`${data.summary.percent_complete}%`} tone="amber" />
            <SummaryStat label="Days to CO (ETA)" value={`${data.summary.days_to_co_eta}d`} tone="slate" />
            <SummaryStat label="Budget" value={$(data.summary.budget_total)} tone="slate" />
            <SummaryStat
              label="Variance"
              value={$(data.summary.budget_variance)}
              tone={data.summary.budget_variance > 0 ? "rose" : "green"}
            />
          </div>

          {/* Stages list */}
          <div className="space-y-2">
            {data.stages.map((s) => (
              <StageRow
                key={s.key}
                stage={s}
                onPatch={(u) => patchStage(s.key, u)}
                editing={editingKey === s.key}
                onEdit={() => setEditingKey(editingKey === s.key ? null : s.key)}
              />
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function StageRow({ stage, onPatch, editing, onEdit }) {
  const st = STAGE_STATUS_STYLE[stage.status] || STAGE_STATUS_STYLE.pending;
  const Icon = st.icon;

  const [localBudget, setLocalBudget] = useState(stage.budget ?? "");
  const [localActual, setLocalActual] = useState(stage.actual_spent ?? "");
  const [localAssigned, setLocalAssigned] = useState(stage.assigned_to ?? "");

  useEffect(() => {
    setLocalBudget(stage.budget ?? "");
    setLocalActual(stage.actual_spent ?? "");
    setLocalAssigned(stage.assigned_to ?? "");
  }, [stage.budget, stage.actual_spent, stage.assigned_to]);

  const save = () => {
    const patch = {};
    if (localBudget !== "" && Number(localBudget) !== stage.budget) patch.budget = Number(localBudget);
    if (localActual !== "" && Number(localActual) !== stage.actual_spent) patch.actual_spent = Number(localActual);
    if (localAssigned !== (stage.assigned_to || "")) patch.assigned_to = localAssigned;
    if (Object.keys(patch).length) onPatch(patch);
    onEdit();
  };

  const cycleStatus = () => {
    const next = { pending: "in_progress", in_progress: "complete", complete: "pending", blocked: "pending" }[stage.status] || "pending";
    onPatch({ status: next });
  };

  const varianceVsBudget = (stage.actual_spent != null && stage.budget != null)
    ? stage.actual_spent - stage.budget
    : null;

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 transition-all"
      data-testid={`stage-row-${stage.key}`}
    >
      <div className="flex items-center gap-3">
        {/* Status pill (clickable to cycle) */}
        <button
          type="button"
          onClick={cycleStatus}
          data-testid={`stage-status-${stage.key}`}
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap hover:brightness-105 transition"
          style={{ color: st.color, backgroundColor: st.bg }}
          title="Click to cycle status"
        >
          <Icon className="w-3 h-3" />
          {st.label}
        </button>

        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-slate-900 leading-tight">
            {stage.order + 1}. {stage.label}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {stage.assigned_to || "Unassigned"} · ~{stage.typical_days}d typical
            {stage.actual_date && <> · <span className="text-emerald-700">done {new Date(stage.actual_date).toLocaleDateString()}</span></>}
          </div>
        </div>

        {/* Money */}
        <div className="text-right text-[12px] whitespace-nowrap">
          <div className="text-slate-500">Budget {$(stage.budget)}</div>
          <div className={"font-semibold " +
            (stage.actual_spent == null ? "text-slate-400" :
              varianceVsBudget > 0 ? "text-rose-700" : "text-emerald-700")}>
            Actual {$(stage.actual_spent)}
          </div>
        </div>

        <button type="button" onClick={onEdit}
                data-testid={`stage-edit-${stage.key}`}
                className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 pl-2">
          {editing ? "Close" : "Edit"}
        </button>
      </div>

      {editing && (
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <MiniField label="Budget">
            <input type="number" value={localBudget}
                   onChange={(e) => setLocalBudget(e.target.value)}
                   data-testid={`stage-budget-input-${stage.key}`}
                   className="w-full rounded border border-slate-200 px-2 py-1.5 text-[12.5px] tabular-nums"/>
          </MiniField>
          <MiniField label="Actual Spent">
            <input type="number" value={localActual}
                   onChange={(e) => setLocalActual(e.target.value)}
                   data-testid={`stage-actual-input-${stage.key}`}
                   className="w-full rounded border border-slate-200 px-2 py-1.5 text-[12.5px] tabular-nums"/>
          </MiniField>
          <MiniField label="Assigned To">
            <input type="text" value={localAssigned}
                   onChange={(e) => setLocalAssigned(e.target.value)}
                   placeholder="GC / subcontractor name"
                   data-testid={`stage-assigned-input-${stage.key}`}
                   className="w-full rounded border border-slate-200 px-2 py-1.5 text-[12.5px]"/>
          </MiniField>
          <div className="col-span-full flex items-center justify-end gap-2">
            <button type="button" onClick={onEdit} className="text-[12px] text-slate-500 hover:text-slate-800">Cancel</button>
            <button type="button" onClick={save}
                    data-testid={`stage-save-${stage.key}`}
                    className="rounded text-white text-[12px] font-semibold px-3 py-1.5"
                    style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CARD 2 · PHILLY INTELLIGENCE
// ═══════════════════════════════════════════════════════════════════════

function PhillyIntelCard({ property }) {
  const [zoning, setZoning] = useState("");
  const [expanded, setExpanded] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const zipCode = property?.address?.zip_code || "";
  const arv = property?.estimated_arv || 0;

  const analyze = useCallback(async (zoningCode) => {
    if (!zipCode && !zoningCode && !arv) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/philly/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          zip_code:    zipCode || null,
          zoning_code: zoningCode || null,
          arv:         arv || null,
        }),
      });
      setData(await res.json());
    } catch {
      // silent — this is public data lookup
    } finally {
      setLoading(false);
    }
  }, [zipCode, arv]);

  useEffect(() => { analyze(zoning); }, [analyze, zoning]);

  return (
    <Card
      icon={<TreePine className="w-4 h-4" />}
      title="Philly Intelligence"
      subtitle="Opportunity Zone · Zoning · 10-year Tax Abatement"
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
      testid="philly-intel-card"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">Address ZIP</div>
          <div className="text-[15px] font-semibold text-slate-900 mt-0.5">{zipCode || "— (add address to workspace)"}</div>

          <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mt-4">Zoning Code</div>
          <input
            value={zoning}
            onChange={(e) => setZoning(e.target.value)}
            placeholder="e.g. RSA-5, RM-2, CMX-2"
            data-testid="philly-zoning-input"
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] uppercase focus:outline-none focus:ring-2 focus:ring-amber-400"
          />

          <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mt-4">Est. ARV (from property)</div>
          <div className="text-[15px] font-semibold text-slate-900 mt-0.5">{$(arv) || "— (add ARV to see abatement savings)"}</div>
        </div>

        <div className="space-y-3">
          {loading && <SkeletonRow />}

          {data?.opportunity_zone && (
            <div className={"rounded-lg border p-3 " +
              (data.opportunity_zone.is_in_oz
                ? "border-emerald-300 bg-emerald-50"
                : "border-slate-200 bg-slate-50")}>
              <div className="flex items-center gap-2">
                <ShieldCheck className={"w-4 h-4 " + (data.opportunity_zone.is_in_oz ? "text-emerald-700" : "text-slate-400")} />
                <div className="text-[12.5px] font-bold text-slate-900">
                  {data.opportunity_zone.is_in_oz ? "IN Opportunity Zone" : "Not in Opportunity Zone (per ZIP)"}
                </div>
              </div>
              {data.opportunity_zone.is_in_oz && (
                <p className="mt-1 text-[11.5px] text-emerald-800">
                  Capital gains reinvested here can defer + reduce federal tax. Hold 10+ years = zero tax on the OZ investment&apos;s appreciation.
                </p>
              )}
            </div>
          )}

          {data?.zoning && !data.zoning.unknown && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-amber-700" />
                <div className="text-[12.5px] font-bold text-slate-900">
                  {data.zoning.code} · {data.zoning.family}
                </div>
              </div>
              <div className="mt-1 text-[11.5px] text-slate-700">{data.zoning.description}</div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-[10.5px] text-slate-600">
                <div><b>{data.zoning.max_units_per_lot}</b> max units</div>
                <div><b>{data.zoning.max_height_ft}ft</b> max height</div>
                <div><b>{data.zoning.min_lot_sqft}</b> min sqft</div>
              </div>
              <div className="mt-2 text-[11px] italic text-slate-500">{data.zoning.notes}</div>
            </div>
          )}
          {data?.zoning?.unknown && zoning && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11.5px] text-slate-600">
              Zoning code &quot;{zoning}&quot; not in our reference. Check atlas.phila.gov.
            </div>
          )}

          {data?.tax_abatement && arv > 0 && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-emerald-700" />
                <div className="text-[12.5px] font-bold text-emerald-900">10-Year Abatement Savings</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11.5px]">
                <div>
                  <div className="text-slate-600">Full annual tax</div>
                  <div className="font-bold tabular-nums">{$(data.tax_abatement.full_annual_tax)}</div>
                </div>
                <div>
                  <div className="text-slate-600">Abated annual tax</div>
                  <div className="font-bold tabular-nums text-emerald-700">{$(data.tax_abatement.abated_annual_tax)}</div>
                </div>
                <div>
                  <div className="text-slate-600">Save / mo</div>
                  <div className="font-bold tabular-nums text-emerald-700">{$(data.tax_abatement.monthly_savings)}</div>
                </div>
                <div>
                  <div className="text-slate-600">Total 10-yr savings</div>
                  <div className="font-bold text-[15px] tabular-nums text-emerald-700">{$(data.tax_abatement.total_10yr_savings)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CARD 3 · AFFORDABLE BUYER QUALIFIER
// ═══════════════════════════════════════════════════════════════════════

function BuyerQualifierCard() {
  const [expanded, setExpanded] = useState(true);
  const [inputs, setInputs] = useState({
    annual_income: 95000, household_size: 3, credit_score: 680,
    first_time_homebuyer: true, is_veteran: false,
    target_purchase_price: 350000, in_philadelphia_city: true,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/philly/qualify-buyer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(inputs),
      });
      setResult(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [inputs]);

  useEffect(() => { run(); }, [run]);

  const update = (k, v) => setInputs((p) => ({ ...p, [k]: v }));

  return (
    <Card
      icon={<Users className="w-4 h-4" />}
      title="Affordable Buyer Qualifier"
      subtitle="AMI · FHA · VA · USDA · PHFA · DPA"
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
      testid="buyer-qualifier-card"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-4">
        {/* Inputs */}
        <div className="space-y-2">
          <MiniField label="Annual Income">
            <input type="number" value={inputs.annual_income}
                   onChange={(e) => update("annual_income", Number(e.target.value))}
                   data-testid="buyer-income"
                   className="w-full rounded border border-slate-200 px-2 py-1.5 text-[13px] tabular-nums"/>
          </MiniField>
          <div className="grid grid-cols-2 gap-2">
            <MiniField label="Household Size">
              <input type="number" min={1} max={8} value={inputs.household_size}
                     onChange={(e) => update("household_size", Number(e.target.value))}
                     data-testid="buyer-hh-size"
                     className="w-full rounded border border-slate-200 px-2 py-1.5 text-[13px] tabular-nums"/>
            </MiniField>
            <MiniField label="Credit Score">
              <input type="number" min={300} max={850} value={inputs.credit_score}
                     onChange={(e) => update("credit_score", Number(e.target.value))}
                     data-testid="buyer-credit"
                     className="w-full rounded border border-slate-200 px-2 py-1.5 text-[13px] tabular-nums"/>
            </MiniField>
          </div>
          <MiniField label="Target Purchase Price">
            <input type="number" value={inputs.target_purchase_price}
                   onChange={(e) => update("target_purchase_price", Number(e.target.value))}
                   data-testid="buyer-price"
                   className="w-full rounded border border-slate-200 px-2 py-1.5 text-[13px] tabular-nums"/>
          </MiniField>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input type="checkbox" checked={inputs.first_time_homebuyer}
                   onChange={(e) => update("first_time_homebuyer", e.target.checked)}
                   data-testid="buyer-ftb" />
            First-time homebuyer
          </label>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input type="checkbox" checked={inputs.is_veteran}
                   onChange={(e) => update("is_veteran", e.target.checked)}
                   data-testid="buyer-vet" />
            Qualifying veteran
          </label>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input type="checkbox" checked={inputs.in_philadelphia_city}
                   onChange={(e) => update("in_philadelphia_city", e.target.checked)}
                   data-testid="buyer-in-city" />
            Property inside Philadelphia city limits
          </label>
        </div>

        {/* Results */}
        <div className="space-y-3">
          {loading && <SkeletonRow />}
          {result && (
            <>
              {/* AMI band */}
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10.5px] font-bold uppercase tracking-widest text-amber-700">Income Band</div>
                    <div className="text-[15px] font-bold text-slate-900 mt-0.5">
                      {result.ami_analysis.band || "—"}
                    </div>
                    <div className="text-[11.5px] text-slate-600 mt-0.5">
                      {result.ami_analysis.buyer_ami_pct}% of AMI ({$(result.ami_analysis.ami_100_for_hh)} = 100%)
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">Qualifies for</div>
                    <div className="text-[24px] font-bold text-emerald-700 tabular-nums leading-none">
                      {result.qualifying_program_count}
                    </div>
                    <div className="text-[10.5px] text-slate-500">programs</div>
                  </div>
                </div>
              </div>

              {/* Program list */}
              <div className="space-y-1.5">
                {result.programs.map((p) => (
                  <div key={p.code}
                       className={"rounded-lg border p-2.5 flex items-start gap-2.5 " +
                         (p.qualifies ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50")}>
                    <div className={"mt-0.5 " + (p.qualifies ? "text-emerald-600" : "text-slate-400")}>
                      {p.qualifies ? <Check className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={"text-[12.5px] font-bold " + (p.qualifies ? "text-emerald-900" : "text-slate-500")}>
                        {p.name}
                      </div>
                      <div className="text-[10.5px] text-slate-600">{p.notes}</div>
                      {!p.qualifies && p.reasons_denied.length > 0 && (
                        <div className="text-[10.5px] text-rose-700 mt-0.5">
                          Why not: {p.reasons_denied.join(" · ")}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-[10.5px] text-slate-600 whitespace-nowrap">
                      <div>{p.min_down_pct}% down</div>
                      <div>≥{p.min_credit} credit</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* DPA */}
              {result.dpa_programs.length > 0 && (
                <div>
                  <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">
                    Down Payment Assistance ({result.dpa_programs.length})
                  </div>
                  <div className="space-y-1">
                    {result.dpa_programs.map((d) => (
                      <div key={d.code} className="rounded border border-emerald-200 bg-white p-2 flex items-start gap-2">
                        <TrendingUp className="w-3.5 h-3.5 mt-0.5 text-emerald-600" />
                        <div className="flex-1 min-w-0 text-[11.5px]">
                          <div className="font-bold text-slate-900">{d.name} · up to {$(d.amount_up_to)}</div>
                          <div className="text-slate-600">{d.notes}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SHARED SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════

function Card({ icon, title, subtitle, expanded, onToggle, children, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={testid}>
      <button
        type="button" onClick={onToggle}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-sm shrink-0"
             style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
          {icon}
        </div>
        <div className="text-left flex-1 min-w-0">
          <div className="text-[14px] font-bold text-slate-900 leading-tight">{title}</div>
          <div className="text-[11.5px] text-slate-500 truncate">{subtitle}</div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="border-t border-slate-100 p-5">{children}</div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, tone = "slate" }) {
  const toneClasses = { slate: "text-slate-900", amber: "text-amber-700", green: "text-emerald-700", rose: "text-rose-700" }[tone];
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
      <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-[16px] font-bold tabular-nums ${toneClasses}`}>{value}</div>
    </div>
  );
}

function MiniField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
      {children}
    </label>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-2 text-slate-500 text-[13px]">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
}
